import React from "react";
import {PageHeader} from "components/molecules/PageHeader/PageHeader";
import {useDispatch, useSelector} from 'react-redux';
import {
  selectStrategiesNotOwned
} from 'redux/strategy/strategySlice';
import {Box} from "@mui/system";
import Typography from "@mui/material/Typography";
import BoardTable from "components/atoms/BoardTable/BoardTable";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import {Chip, Grid, Link, TableSortLabel, Skeleton} from "@mui/material";
import TableBody from "@mui/material/TableBody";
import Button from "@mui/material/Button";
import {Link as RouterLink} from "react-router-dom";
import {routes} from "routes";
import {
  formatCurrency,
  formatPercentage,
  formatRatio,
  getValueColor
} from "utils/metricFormatters";

// Helper function to safely convert values to numbers
const safeParseFloat = (value) => {
  if (value === null || value === undefined) return 0;
  // Handle both string and number inputs
  const numberValue = typeof value === 'string' ? parseFloat(value) : Number(value);
  return isNaN(numberValue) ? 0 : numberValue;
};

export default function LeaderBoard() {
  const dispatch = useDispatch();
  const allStrategies = useSelector(selectStrategiesNotOwned);
  const isAuthorised = useSelector(state => state.global.isAuthorised);
  const loading = useSelector(state => state.strategy.loading);

  return (
    <>
      <PageHeader text1={"Leader"} text2={"Board"}  description={"Trading is risky and you can lose money."}/>

      <BoardTable>
        <TableHead>
          <TableRow>
            <TableCell>Strategy</TableCell>
            <TableCell align="center"><TableSortLabel active={true} direction="desc">Current Size</TableSortLabel></TableCell>
            <TableCell align="center"><TableSortLabel>Category</TableSortLabel></TableCell>
            <TableCell align="center"><TableSortLabel>Worst Drawdown</TableSortLabel></TableCell>
            <TableCell align="center"><TableSortLabel>Annual Return</TableSortLabel></TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {allStrategies.map((strategy) => {
            // Get metrics from strategy object
            const metrics = strategy.performance_metrics || {};
            
            // Format metrics with appropriate formatters
            const currentValue = formatCurrency(safeParseFloat(metrics.value), 0);
            // Use the category name directly from the strategy object
            const categoryLabel = strategy.category_display || strategy.category;
            const drawdownPct = formatPercentage(safeParseFloat(metrics.drawdown?.percentage), 1);
            const annualReturn = formatPercentage(safeParseFloat(metrics.annual_return), 1);
            
            // Get colors for colored metrics
            const drawdownColor = 'error.main'; // Always red for drawdown
            const annualReturnColor = getValueColor(safeParseFloat(metrics.annual_return));
            
            return (
              <TableRow key={`strategy-${strategy.id}`} >
                <TableCell className="light2" >
                  <Grid container >
                    <Grid item md={12} lg={6}>
                      <RouterLink to={routes.strategyDetails.replace(":strategyId", strategy.id)}>
                        <Typography variant="h4" sx={{mb:1}} color="primary"> {strategy.name.toUpperCase()} </Typography>
                      </RouterLink>
                      {strategy.trade_type.map((value, index) => (
                        <Chip key={`strategy-${strategy.id}-type-${index}`} label={value.toUpperCase()} size="small" variant="outlined" sx={{mr: 0.5}}/>
                      ))}
                    </Grid>
                    <Grid item md={12} lg={6} >
                      <Box display="flex" justifyContent={{xs: "flex-start", lg: "flex-end"}}  mt={{xs: 1, lg: 0}} >
                        { isAuthorised &&
                          <>
                            <RouterLink to={routes.strategyDetails.replace(":strategyId", strategy.id)}>
                              <Button variant="outlined" size="small" > Details </Button><br/>
                            </RouterLink>
                          </>
                        }
                      </Box>
                    </Grid>
                  </Grid>

                  </TableCell>
                  <TableCell align="center" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="largeText" color="secondary.dark">{currentValue}</Typography>
                    )}
                    <br/>
                    <Typography variant="smallText">Current Size</Typography>
                  </TableCell>
                  <TableCell align="center" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="largeText" color="secondary.dark">{categoryLabel}</Typography>
                    )}
                    <br/>
                    <Typography variant="smallText">Category</Typography>
                  </TableCell>
                  <TableCell align="center" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="largeText" color={drawdownColor}>{drawdownPct}</Typography>
                    )}
                    <br/>
                    <Typography variant="smallText">Worst Drawdown</Typography>
                  </TableCell>
                  <TableCell align="center" className="light">
                    {loading ? (
                      <Skeleton width={80} height={24} />
                    ) : (
                      <Typography variant="largeText" color={annualReturnColor}>{annualReturn}</Typography>
                    )}
                    <br/>
                    <Typography variant="smallText">
                      <Box element="span" sx={{ fontWeight: "500" }}>Annual Return</Box>
                    </Typography>
                  </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </BoardTable>
    </>
  )
}